import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { PageHeader } from '../components/PageHeader';
import { LoadReportView, SaveTable, saveBtn } from '../components/SaveTable';
import { errorText, useApi } from '../lib/api';
import {
  applyKindFilter,
  deleteConfirmText,
  loadConfirmText,
  parseSaveFile,
  TesterToolsMissing,
  type DevSaveLoadReport,
  type DevSaveRow,
  type KindFilter,
} from '../lib/devSaves';
import {
  deleteSave,
  exportSave,
  findPlayers,
  importSave,
  isNameTaken,
  LIST_TAKE,
  listSaves,
  loadSave,
  saveGraph,
} from '../lib/devSavesApi';

const input = 'rounded border-2 border-ink bg-white px-2 py-1';
const noRetryWhenOff = (n: number, e: Error) => !(e instanceof TesterToolsMissing) && n < 2;

/**
 * Save points (tester tools): every save, searchable and filterable by kind (automatic ones hidden by default), with
 * load onto any player, export and delete; importing an exported file; and what a save covers (`/dev/saves/graph`).
 */
export function SavePointsPage() {
  const { client } = useApi();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<KindFilter>('');
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [report, setReport] = useState<{ title: string; report: DevSaveLoadReport } | null>(null);

  const saves = useQuery({
    queryKey: ['dev-saves', 'list', q, kind],
    queryFn: () => listSaves(client, { q, kind }),
    retry: noRetryWhenOff,
  });

  const remove = useMutation({
    mutationFn: (s: DevSaveRow) => deleteSave(client, s.id),
    onSuccess: () => {
      setProblem(null);
      void qc.invalidateQueries({ queryKey: ['dev-saves'] });
    },
    onError: (e) => setProblem(errorText(e)),
  });
  const download = useMutation({
    mutationFn: (s: DevSaveRow) => exportSave(client, s),
    onError: (e) => setProblem(errorText(e)),
  });

  const header = (
    <PageHeader
      title="Save points"
      description="Tester tools: named snapshots of everything a player owns. Load one onto any player (an automatic save of their state is taken first), export it as a file, or import one."
    />
  );

  if (saves.error instanceof TesterToolsMissing) {
    return (
      <>
        {header}
        <p className="rounded border-2 border-ink bg-white p-4 text-sm">{saves.error.message}</p>
      </>
    );
  }

  const rows = saves.data ? applyKindFilter(saves.data.items, kind) : [];

  return (
    <>
      {header}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <input
          aria-label="Search saves"
          placeholder="Name, description or player"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={`${input} w-full max-w-md py-2`}
        />
        <select
          aria-label="Kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as KindFilter)}
          className={`${input} py-2`}
        >
          <option value="">manual and import</option>
          <option value="manual">manual</option>
          <option value="import">import</option>
          <option value="auto">automatic</option>
          <option value="all">all, including automatic</option>
        </select>
      </div>

      <ImportForm onProblem={setProblem} />

      {problem && (
        <p className="mb-2 text-sm text-blam" role="alert">
          {problem}
        </p>
      )}
      {report && (
        <LoadReportView
          title={report.title}
          report={report.report}
          onClose={() => setReport(null)}
        />
      )}

      <section className="mt-4 overflow-x-auto rounded border-2 border-ink bg-white p-4">
        {saves.isPending ? (
          <p className="text-sm">Loading&hellip;</p>
        ) : saves.isError ? (
          <p className="text-sm text-blam">{errorText(saves.error)}</p>
        ) : (
          <>
            <p className="text-xs text-bruise">
              {rows.length} shown
              {saves.data.total > LIST_TAKE ? ` (newest ${LIST_TAKE} of ${saves.data.total})` : ''}
            </p>
            <SaveTable
              rows={rows}
              actions={(s) => (
                <>
                  <button
                    type="button"
                    className={saveBtn}
                    onClick={() => setLoading(loading === s.id ? null : s.id)}
                  >
                    Load onto&hellip;
                  </button>
                  <button
                    type="button"
                    className={saveBtn}
                    disabled={download.isPending}
                    onClick={() => download.mutate(s)}
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    className={saveBtn}
                    disabled={remove.isPending}
                    onClick={() => confirm(deleteConfirmText(s)) && remove.mutate(s)}
                  >
                    Delete
                  </button>
                </>
              )}
              expanded={(s) =>
                loading === s.id ? (
                  <LoadOnto
                    save={s}
                    onDone={(title, r) => {
                      setLoading(null);
                      setReport({ title, report: r });
                    }}
                  />
                ) : null
              }
            />
          </>
        )}
      </section>

      <GraphPanel />
    </>
  );
}

/** "Load onto…": search a player (`/dev/saves/players`), pick one, confirm, load. Every query is refetched after. */
function LoadOnto({
  save,
  onDone,
}: {
  save: DevSaveRow;
  onDone: (title: string, report: DevSaveLoadReport) => void;
}) {
  const { client } = useApi();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const matches = useQuery({
    queryKey: ['dev-saves', 'players', q.trim()],
    queryFn: () => findPlayers(client, q.trim()),
    enabled: q.trim().length >= 2,
  });
  const load = useMutation({
    mutationFn: (p: { playerId: string; label: string }) => loadSave(client, save.id, p.playerId),
    onSuccess: (r, p) => {
      void qc.invalidateQueries();
      onDone(`Loaded "${save.name}" onto ${p.label}`, r);
    },
    onError: (e) => setProblem(errorText(e)),
  });

  return (
    <div className="text-sm">
      <input
        aria-label={`Find a player to load ${save.name} onto`}
        placeholder="player id, email or name"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className={`${input} w-80 max-w-full`}
      />
      {matches.isError && <p className="text-blam">{errorText(matches.error)}</p>}
      {matches.data && matches.data.length === 0 && (
        <p className="text-bruise">No players match.</p>
      )}
      <ul className="mt-1 space-y-1">
        {matches.data?.map((p) => {
          const label = p.email ? `${p.displayName} (${p.email})` : p.displayName;
          return (
            <li key={p.playerId} className="flex items-center gap-2">
              <button
                type="button"
                className={saveBtn}
                disabled={load.isPending}
                onClick={() =>
                  confirm(loadConfirmText(save, label, p.playerId === save.sourcePlayerId)) &&
                  load.mutate({ playerId: p.playerId, label })
                }
              >
                Load
              </button>
              <span>{label}</span>
              <span className="font-mono text-xs text-bruise">{p.playerId}</span>
            </li>
          );
        })}
      </ul>
      {problem && (
        <p className="mt-1 text-blam" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}

/** Import an exported `*.save.json` (optionally renamed); a taken name asks to overwrite. */
function ImportForm({ onProblem }: { onProblem: (p: string | null) => void }) {
  const { client } = useApi();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (a: { file: ReturnType<typeof parseSaveFile>; overwrite: boolean }) => {
      if (!a.file.ok) throw new Error(a.file.error);
      return importSave(client, {
        file: a.file.file,
        name: name.trim() || null,
        overwrite: a.overwrite,
      });
    },
    onSuccess: (row) => {
      onProblem(null);
      setDone(`Imported "${row.name}".`);
      setName('');
      void qc.invalidateQueries({ queryKey: ['dev-saves'] });
    },
    onError: (e, a) => {
      if (!a.overwrite && isNameTaken(e)) {
        if (confirm('A save with that name already exists. Overwrite it?'))
          run.mutate({ ...a, overwrite: true });
        return;
      }
      onProblem(errorText(e));
    },
  });

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setDone(null);
    const parsed = parseSaveFile(await f.text());
    if (!parsed.ok) return onProblem(parsed.error);
    run.mutate({ file: parsed, overwrite: false });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="font-semibold">Import file</span>
      <input
        aria-label="Import name"
        placeholder="name (optional, else the file's)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={`${input} w-64`}
      />
      <input
        aria-label="Save file"
        type="file"
        accept=".json,application/json"
        disabled={run.isPending}
        onChange={(e) => void pick(e)}
        className="text-xs"
      />
      {done && <span className="text-xs">{done}</span>}
    </div>
  );
}

/** What a save covers: owned tables and how they're reached, excluded tables and why, shared tables. */
function GraphPanel() {
  const { client } = useApi();
  const [open, setOpen] = useState(false);
  const graph = useQuery({
    queryKey: ['dev-saves', 'graph'],
    queryFn: () => saveGraph(client),
    enabled: open,
    retry: noRetryWhenOff,
  });
  const excluded = Object.entries(graph.data?.excluded ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  return (
    <details
      className="mt-4 rounded border-2 border-ink bg-white p-4 text-sm"
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="cursor-pointer font-semibold">What a save covers</summary>
      {graph.isPending ? (
        <p className="mt-2">{open ? 'Loading…' : ''}</p>
      ) : graph.isError ? (
        <p className="mt-2 text-blam">{errorText(graph.error)}</p>
      ) : (
        <div className="mt-2 space-y-4 overflow-x-auto">
          <p className="text-xs text-bruise">
            Schema {graph.data.schemaMigration} · save format {graph.data.format}
          </p>
          <div>
            <h3 className="font-semibold">Owned tables ({graph.data.tables.length})</h3>
            <table className="mt-1 w-full text-xs">
              <thead className="text-bruise">
                <tr>
                  <th className="px-2 py-1 text-left">Table</th>
                  <th className="px-2 py-1 text-left">Key</th>
                  <th className="px-2 py-1 text-left">Reached via</th>
                  <th className="px-2 py-1 text-left">Shared references</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {graph.data.tables.map((t) => (
                  <tr key={t.table} className="border-t border-newsprint align-top">
                    <td className="px-2 py-1 font-semibold">{t.table}</td>
                    <td className="px-2 py-1">{t.key.join(', ')}</td>
                    <td className="px-2 py-1">
                      {t.ownedVia.map((v) => (
                        <div key={v}>{v}</div>
                      ))}
                    </td>
                    <td className="px-2 py-1">
                      {t.sharedReferences.map((v) => (
                        <div key={v}>{v}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3 className="font-semibold">Excluded ({excluded.length})</h3>
            <ul className="mt-1 space-y-0.5 text-xs">
              {excluded.map(([table, reason]) => (
                <li key={table}>
                  <span className="font-mono font-semibold">{table}</span>: {reason}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-semibold">Shared, never saved ({graph.data.shared.length})</h3>
            <p className="font-mono text-xs">{graph.data.shared.join(', ') || '-'}</p>
          </div>
        </div>
      )}
    </details>
  );
}
